// Shopify theme sync marker: listing-rewrite-2026-06-21.
import { createSoAtlantaClient, normalizeBusiness, readJsonScript } from '@theme/so-atlanta-api';
import { THIRD_PARTY_CONTENT_TIP, escapeAttr, escapeHtml, renderDisclosure, renderTrustBadges } from '@theme/so-atlanta-renderers';
import { venueProfileUrl } from '@theme/so-atlanta-urls';

class SoAtlantaSearchResults extends HTMLElement {
  connectedCallback() {
    void this.#load();
  }

  async #load() {
    const query = String(this.dataset.query || '').trim();
    const content = this.querySelector('[data-sa-search-content]');
    if (!query || !(content instanceof HTMLElement)) {
      this.hidden = true;
      return;
    }

    const settings = readJsonScript(this, '[data-sa-search-settings]');
    if (settings.enabled === false) {
      this.hidden = true;
      return;
    }

    try {
      const client = createSoAtlantaClient(settings);
      const data = await client.search({ q: query, limit: 12 });
      const sourceListings = data?.listings || data?.businesses;
      const spots = Array.isArray(sourceListings)
        ? sourceListings.map((listing) => normalizeBusiness(listing)).filter((listing) => listing.slug || listing.shopifyUrl)
        : [];
      const uniqueSpots = dedupeAgainstPageLinks(spots, settings.venueDirectoryPath);

      if (!uniqueSpots.length) {
        if (settings.partnerListingUrl) {
          content.innerHTML = renderListingPrompt(query, settings.partnerListingUrl);
          this.hidden = false;
        } else {
          this.hidden = true;
        }
        return;
      }

      content.innerHTML = renderSearchResults(uniqueSpots, settings.venueDirectoryPath, settings.partnerListingUrl);
      hideNativeNoResultsMessage();
      this.hidden = false;
    } catch (error) {
      console.warn('So Atlanta search results failed', error);
      this.hidden = true;
    }
  }
}

if (!customElements.get('so-atlanta-search-results')) {
  customElements.define('so-atlanta-search-results', SoAtlantaSearchResults);
}

function hideNativeNoResultsMessage() {
  document.querySelectorAll('.search-results__no-results').forEach((message) => {
    if (message instanceof HTMLElement) message.hidden = true;
  });
}

/**
 * @param {Record<string, any>[]} spots
 * @param {string | undefined} venueDirectoryPath
 * @returns {Record<string, any>[]}
 */
function dedupeAgainstPageLinks(spots, venueDirectoryPath) {
  const existingUrls = new Set(
    Array.from(document.querySelectorAll('#ResultsList a[href]'))
      .map((link) => normalizePath(link.getAttribute('href')))
      .filter(Boolean)
  );

  return spots.filter((spot) => {
    const href = venueProfileUrl(spot, venueDirectoryPath);
    return href && !existingUrls.has(normalizePath(href));
  });
}

/**
 * @param {Record<string, any>[]} spots
 * @param {string | undefined} venueDirectoryPath
 * @param {string | undefined} partnerListingUrl
 * @returns {string}
 */
function renderSearchResults(spots, venueDirectoryPath, partnerListingUrl) {
  return `
    <section class="sa-search-results__section" aria-labelledby="sa-search-results-title">
      <h2 id="sa-search-results-title" class="sa-search-results__title">
        Discovery Spots
        ${renderDisclosure('Third-party content', THIRD_PARTY_CONTENT_TIP, {
          className: 'sa-search-results__disclosure sa-disclosure--icon-only',
        })}
      </h2>
      <ul class="sa-search-results__grid" role="list">
        ${spots.map((spot) => renderSearchCard(spot, venueDirectoryPath)).join('')}
      </ul>
      ${partnerListingUrl ? renderListingPrompt(null, partnerListingUrl) : ''}
    </section>
  `;
}

/**
 * @param {string | null} query
 * @param {string} partnerListingUrl
 * @returns {string}
 */
function renderListingPrompt(query, partnerListingUrl) {
  const copy = query
    ? `Do you manage a business that should appear for "${query}"?`
    : 'Own a business that should be listed here?';
  return `
    <div class="sa-search-results__partner-callout">
      <span>${escapeHtml(copy)}</span>
      <a href="${escapeAttr(partnerListingUrl)}" target="_blank" rel="noopener noreferrer">Get listed on So Atlanta</a>
    </div>
  `;
}

/**
 * @param {Record<string, any>} spot
 * @param {string | undefined} venueDirectoryPath
 * @returns {string}
 */
function renderSearchCard(spot, venueDirectoryPath) {
  const href = venueProfileUrl(spot, venueDirectoryPath);
  const title = spot.name || spot.title || spot.businessName || 'Untitled spot';
  const image = spot.fallbackImage || spot.imageUrl || spot.logoImage || '';
  const category = spot.category || spot.categories?.[0] || 'Discovery Spot';
  const location = spot.neighborhood || spot.address || '';
  const description = spot.shortDescription || spot.description || spot.whyGo || spot.locationSummary || '';
  const trustBadges = renderTrustBadges(spot.trustBadges, 'sa-search-results__kicker');

  return `
    <li class="sa-search-results__item">
      <a class="sa-search-results__card" href="${escapeAttr(href)}">
        <span class="sa-search-results__media" aria-hidden="true">
          ${
            image
              ? `<img src="${escapeAttr(image)}" alt="" loading="lazy">`
              : `<span>${escapeHtml(String(title).slice(0, 1).toUpperCase())}</span>`
          }
        </span>
        <span class="sa-search-results__body">
          <span class="sa-search-results__kicker">${escapeHtml(category)}</span>
          <span class="sa-search-results__name">${escapeHtml(title)}</span>
          ${trustBadges}
          ${location ? `<span class="sa-search-results__meta">${escapeHtml(location)}</span>` : ''}
          ${description ? `<span class="sa-search-results__text">${escapeHtml(description)}</span>` : ''}
        </span>
      </a>
    </li>
  `;
}

/**
 * @param {string | null} href
 * @returns {string}
 */
function normalizePath(href) {
  if (!href) return '';

  try {
    const url = new URL(href, window.location.origin);
    return url.pathname.replace(/\/+$/, '');
  } catch (_) {
    return String(href).replace(/\/+$/, '');
  }
}
